import { RedlineConfigValues } from '../../model/CommonModel';
import {
	BadgeStudioSpaceObjects,
	GraphicActionType,
	GraphicDirectoryInfo,
	GraphicPageMode,
	ObjectLibraryTreeNode,
	SetBadgeStudioBarCodePayload,
	SetBadgeStudioImagePayload,
	SetBadgeStudioInfoPayload,
	SetBadgeStudioMagstripePayload,
	SetGraphicTextPayload,
	SetVisualShapeObjectPayload,
} from '../../model/GraphicModel';
import { UserDefinedFieldSetup } from '../../model/UserDefinedFields';
import { BadgeActionTypes } from './actionsTypes';

type CommonActionTypes<T extends BadgeActionTypes, K> = {
	type: T;
	payload: K;
};

export type SetActionType = CommonActionTypes<BadgeActionTypes.SET_ACTION_TYPE, GraphicActionType>;
export type SetGraphicText = CommonActionTypes<BadgeActionTypes.SET_GRAPHIC_TEXT, SetGraphicTextPayload>;
export type SetBadgeStudioMagstripe = CommonActionTypes<BadgeActionTypes.SET_BADGE_STUDIO_MAGSTRIPE, SetBadgeStudioMagstripePayload>;
export type SetVisualShapeObjects = CommonActionTypes<BadgeActionTypes.SET_VISUAL_SHAPE_OBJECT, SetVisualShapeObjectPayload>;
export type SetBadgeStudioImage = CommonActionTypes<BadgeActionTypes.SET_BADGE_STUDIO_IMAGE, SetBadgeStudioImagePayload>;
export type SetBadgeStudioBarCode = CommonActionTypes<BadgeActionTypes.SET_BADGE_STUDIO_BARCODE, SetBadgeStudioBarCodePayload>;
export type SetBadgeStudioInfo = CommonActionTypes<BadgeActionTypes.SET_BADGE_STUDIO_INFO, SetBadgeStudioInfoPayload>;
export type SetFonts = CommonActionTypes<BadgeActionTypes.SET_FONTS, string[]>;
export type SetUDFs = CommonActionTypes<BadgeActionTypes.SET_UDFS, UserDefinedFieldSetup[]>;
export type SetUDFs_2_5_6 = CommonActionTypes<BadgeActionTypes.SET_UDFS_256, UserDefinedFieldSetup[]>;
export type SetSpaceObjectDataAction = CommonActionTypes<BadgeActionTypes.SET_SPACE_OBJECT_DATA, Partial<BadgeStudioSpaceObjects>>;
export type SetCurrentRedlineConfigValues = CommonActionTypes<BadgeActionTypes.SET_CURRENT_REDLINE_CONFIG_VALUES, RedlineConfigValues>;
export type SetGraphicData = CommonActionTypes<BadgeActionTypes.SET_GRAPHIC_DATA, Partial<GraphicDirectoryInfo>>;
export type SetDisableMenuOpts = CommonActionTypes<BadgeActionTypes.SET_DISABLE_MENU_OPTS, boolean>;
export type SetDropDataNodes = CommonActionTypes<BadgeActionTypes.SET_DROP_DATA_NODES, ObjectLibraryTreeNode[]>;
export type SetHasChanged = CommonActionTypes<BadgeActionTypes.SET_HAS_CHANGED, boolean>;
export type SetViewMode = CommonActionTypes<BadgeActionTypes.SET_VIEW_MODE, GraphicPageMode>;

const setViewModeAction = (payload: GraphicPageMode): SetViewMode => ({
	type: BadgeActionTypes.SET_VIEW_MODE,
	payload,
});
const setHasChangedAction = (payload: boolean): SetHasChanged => ({
	type: BadgeActionTypes.SET_HAS_CHANGED,
	payload,
});
const setSpaceObjectDataAction = (payload: Partial<BadgeStudioSpaceObjects>): SetSpaceObjectDataAction => ({
	type: BadgeActionTypes.SET_SPACE_OBJECT_DATA,
	payload,
});
const setCurrentRedlineConfigValuesAction = (payload: RedlineConfigValues): SetCurrentRedlineConfigValues => ({
	type: BadgeActionTypes.SET_CURRENT_REDLINE_CONFIG_VALUES,
	payload,
});
const setGraphicDataAction = (payload: Partial<GraphicDirectoryInfo>): SetGraphicData => ({
	type: BadgeActionTypes.SET_GRAPHIC_DATA,
	payload,
});
const setDisableMenuOptsAction = (payload: boolean): SetDisableMenuOpts => ({
	type: BadgeActionTypes.SET_DISABLE_MENU_OPTS,
	payload,
});
const setDropDataNodesAction = (payload: ObjectLibraryTreeNode[]): SetDropDataNodes => ({
	type: BadgeActionTypes.SET_DROP_DATA_NODES,
	payload,
});
const setActionType = (payload: GraphicActionType): SetActionType => ({
	payload,
	type: BadgeActionTypes.SET_ACTION_TYPE,
});
const setGraphicTextAction = (payload: SetGraphicTextPayload): SetGraphicText => ({
	type: BadgeActionTypes.SET_GRAPHIC_TEXT,
	payload,
});
const setBadgeStudioMagstripeAction = (payload: SetBadgeStudioMagstripePayload): SetBadgeStudioMagstripe => ({
	type: BadgeActionTypes.SET_BADGE_STUDIO_MAGSTRIPE,
	payload,
});
const setVisualShapeObjectAction = (payload: SetVisualShapeObjectPayload): SetVisualShapeObjects => ({
	type: BadgeActionTypes.SET_VISUAL_SHAPE_OBJECT,
	payload,
});
const setBadgeStudioImageAction = (payload: SetBadgeStudioImagePayload): SetBadgeStudioImage => ({
	type: BadgeActionTypes.SET_BADGE_STUDIO_IMAGE,
	payload,
});
const setBadgeStudioBarCodeAction = (payload: SetBadgeStudioBarCodePayload): SetBadgeStudioBarCode => ({
	type: BadgeActionTypes.SET_BADGE_STUDIO_BARCODE,
	payload,
});
const setBadgeStudioInfoAction = (payload: SetBadgeStudioInfoPayload): SetBadgeStudioInfo => ({
	type: BadgeActionTypes.SET_BADGE_STUDIO_INFO,
	payload,
});
const setFontsAction = (payload: string[]): SetFonts => ({
	type: BadgeActionTypes.SET_FONTS,
	payload,
});
const setUDFsAction = (payload: UserDefinedFieldSetup[]): SetUDFs => ({
	type: BadgeActionTypes.SET_UDFS,
	payload,
});
const setUDFs_256_Action = (payload: UserDefinedFieldSetup[]): SetUDFs_2_5_6 => ({
	type: BadgeActionTypes.SET_UDFS_256,
	payload,
});

export type BadgeAction =
	| SetActionType
	| SetGraphicText
	| SetBadgeStudioMagstripe
	| SetVisualShapeObjects
	| SetBadgeStudioImage
	| SetBadgeStudioBarCode
	| SetBadgeStudioInfo
	| SetFonts
	| SetUDFs
	| SetUDFs_2_5_6
	| SetSpaceObjectDataAction
	| SetCurrentRedlineConfigValues
	| SetGraphicData
	| SetDisableMenuOpts
	| SetDropDataNodes
	| SetHasChanged
	| SetViewMode;

export {
	setActionType,
	setBadgeStudioBarCodeAction,
	setBadgeStudioImageAction,
	setBadgeStudioInfoAction,
	setBadgeStudioMagstripeAction,
	setCurrentRedlineConfigValuesAction,
	setDisableMenuOptsAction,
	setDropDataNodesAction,
	setFontsAction,
	setGraphicDataAction,
	setGraphicTextAction,
	setHasChangedAction,
	setSpaceObjectDataAction,
	setUDFsAction,
	setUDFs_256_Action,
	setViewModeAction,
	setVisualShapeObjectAction,
};
